import React from 'react';
import { Link } from 'react-router-dom';
import { PhoneIcon, MapPinIcon } from 'lucide-react';
import { PageHero } from '../components/PageHero';
import { SectionHeading } from '../components/SectionHeading';
import { ServiceAreaAccordion } from '../components/ServiceAreaAccordion';
import { CTABand } from '../components/CTABand';
import { counties, featuredCities, countyCities } from '../data/serviceAreas';
import { site, images } from '../data/site';
export function ServiceArea() {
  const totalCities = countyCities.reduce((sum, c) => sum + c.cities.length, 0);
  return <>
      <PageHero eyebrow="Service area" title="Where we work in Michigan" description={`${counties.join(', ')} Counties — ${totalCities} communities across southeast Michigan.`} crumbs={[{
      label: 'Home',
      to: '/'
    }, {
      label: 'Service Area'
    }]} image={images.home} data-id="element-444" data-mp="3003wbhms6ek7" />

      <section className="border-b border-steel-200 bg-white" data-id="element-445" data-mp="2l334zf8i4lm9">
        <div className="mx-auto grid max-w-7xl gap-4 px-6 py-8 sm:grid-cols-2 lg:grid-cols-4" data-id="element-446" data-mp="17kt6yueosxyj">
          {[{
          value: '7',
          label: 'Counties served'
        }, {
          value: String(totalCities),
          label: 'Cities and townships'
        }, {
          value: site.estimatePhone.display,
          label: 'Free estimates',
          href: site.estimatePhone.href
        }, {
          value: site.plumbingPhone.display,
          label: 'Plumbing line',
          href: site.plumbingPhone.href
        }].map(stat => <div key={stat.label} className="border-l-4 border-safety bg-bone px-5 py-4" data-id="element-447" data-mp="1q97w1c5g21wp">
              {stat.href ? <a href={stat.href} className="font-display text-2xl font-bold tracking-tight text-ink hover:text-safety" data-id="element-448" data-mp="1gc3bthv5sgij">
                  {stat.value}
                </a> : <p className="font-display text-3xl font-bold tracking-tight text-ink" data-id="element-449" data-mp="50asj9s38v3y">
                  {stat.value}
                </p>}
              <p className="mt-1 text-xs font-bold uppercase tracking-[0.16em] text-steel-400" data-id="element-450" data-mp="2cieox80xq24m">
                {stat.label}
              </p>
            </div>)}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-12 md:py-16" data-id="element-451" data-mp="22slnv0yf55em">
        <SectionHeading eyebrow="Frequently served" title="Cities we are called to most" description="These communities show up most often on our schedule, but we cover every city listed below." data-id="element-452" data-mp="1xvbqmz4sfpg8" />
        <div className="mt-6 flex flex-wrap gap-2" data-id="element-453" data-mp="1otc63wuke38j">
          {featuredCities.map(city => <span key={city} className="inline-flex items-center gap-1.5 rounded-sm border border-steel-200 bg-white px-3 py-2 text-sm font-medium text-ink-700" data-id="element-454" data-mp="1jvtyc9sa9u24">
              <MapPinIcon className="h-3.5 w-3.5 text-safety" aria-hidden="true" data-id="element-455" data-mp="3e8jrgpatuz8g" />
              {city}
            </span>)}
        </div>

        <div className="mt-12" data-id="element-456" data-mp="3gtrjc21k36o5">
          <SectionHeading eyebrow="Full coverage" title="County by county" description="Open a county to see every city and township we cover, including for plumbing service." data-id="element-457" data-mp="3gsghi7ro88zo" />
          <div className="mt-6" data-id="element-458" data-mp="3qjt5goacmf0f">
            <ServiceAreaAccordion data-id="element-459" data-mp="3vie80ud2pas1" />
          </div>
        </div>
      </section>

      <section className="border-t border-steel-200 bg-white" data-id="element-460" data-mp="3vkgvb6mucpt2">
        <div className="mx-auto grid max-w-7xl gap-8 px-6 py-12 lg:grid-cols-2 lg:items-center" data-id="element-461" data-mp="2xvxe7z3jtfb1">
          <div data-id="element-462" data-mp="32urxf25fh5vo">
            <h2 className="font-display text-3xl font-bold uppercase leading-tight tracking-tight text-ink" data-id="element-463" data-mp="32u8lc22glbpk">
              Plumbing service across all seven counties
            </h2>
            <p className="mt-3 text-base leading-relaxed text-steel-500" data-id="element-464" data-mp="3cso49hrc1enz">
              Emergency plumbing service, sewer and drain cleaning, frozen and
              burst pipes, flooding basements, water heaters and full plumbing
              inspections are available throughout the county and city lists
              above.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row" data-id="element-465" data-mp="potsvsau8l6a">
              <a href={site.plumbingPhone.href} className="flex items-center justify-center gap-2 rounded-sm bg-safety px-6 py-4 text-lg font-bold tracking-wide text-white transition hover:bg-safety-600" data-id="element-466" data-mp="lmzyu2v5eff3">
                <PhoneIcon className="h-5 w-5" aria-hidden="true" data-id="element-467" data-mp="18lcxc4ap5kwi" />
                {site.plumbingPhone.display}
              </a>
              <Link to="/services/plumbing" className="flex items-center justify-center gap-2 rounded-sm border-2 border-ink px-6 py-4 text-base font-bold uppercase tracking-wide text-ink transition hover:bg-ink hover:text-white" data-id="element-468" data-mp="14jhx734i5dkb">
                Plumbing services
              </Link>
            </div>
          </div>
          <img src={images.plumbing} alt="Plumbing service work" className="h-64 w-full rounded-sm border border-steel-200 object-cover lg:h-80" data-id="element-469" data-mp="1nb0wozzbi9hl" />
        </div>
      </section>

      <CTABand title="Not sure if you are in our area?" description="Call the estimate line and tell us your city — we will let you know." data-id="element-470" data-mp="2ax4ma05tqv7y" />
    </>;
}